import { create } from 'zustand';

/**
 * A live snapshot the running RoutineTimer publishes each tick so the minimized
 * header widget can render time + XP without owning the timer engine. The engine
 * state itself stays in the (globally-mounted, hidden-when-minimized)
 * RoutineExecution component — see RoutineSessionManager.
 */
export interface RoutineRuntimeSnapshot {
  /** Current step label — the habit name, or "Break". */
  stepLabel: string;
  /** Routine category id (drives the widget accent colour). */
  category: string;
  /** Seconds remaining in the current step. */
  timeRemaining: number;
  /** XP accrued so far this run. */
  earnedXP: number;
  stepIndex: number;
  stepCount: number;
  /** Label of the next step (null on the last step). Drives the header "Next" pill. */
  nextLabel: string | null;
  /** Category of the next step (null on the last step) — colours the "Next" pill. */
  nextCategory: string | null;
}

interface RoutineSessionState {
  /** The routine currently being run (null = no active run). */
  activeRoutineId: string | null;
  /** Whether the full-screen execution view is shown (false = minimized to the widget). */
  expanded: boolean;
  /** Single source of truth for pause — shared by the full view and the widget. */
  isPaused: boolean;
  /** Set by the widget's Stop button; observed by RoutineTimer to finish early. */
  stopRequested: boolean;
  /** Skip the preview and drop straight into the active timer (hover-Play path). */
  autoStart: boolean;
  runtime: RoutineRuntimeSnapshot | null;

  start: (routineId: string, autoStart?: boolean) => void;
  expand: () => void;
  minimize: () => void;
  togglePause: () => void;
  setPaused: (paused: boolean) => void;
  requestStop: () => void;
  clearStopRequest: () => void;
  setRuntime: (runtime: RoutineRuntimeSnapshot | null) => void;
  /** Fully clear the session (run finished or abandoned). */
  stop: () => void;
}

// Not persisted: the timer's authoritative state lives in React (RoutineExecution),
// which can't be rehydrated, so persisting only the shell would create a broken
// half-state on reload. Cloud run-history is a separate, deferred concern.
export const useRoutineSessionStore = create<RoutineSessionState>((set) => ({
  activeRoutineId: null,
  expanded: false,
  isPaused: true,
  stopRequested: false,
  autoStart: false,
  runtime: null,

  start: (routineId, autoStart = false) =>
    set({ activeRoutineId: routineId, expanded: true, isPaused: true, stopRequested: false, autoStart, runtime: null }),
  expand: () => set({ expanded: true }),
  minimize: () => set({ expanded: false }),
  togglePause: () => set((s) => ({ isPaused: !s.isPaused })),
  setPaused: (isPaused) => set({ isPaused }),
  requestStop: () => set({ stopRequested: true, expanded: true }),
  clearStopRequest: () => set({ stopRequested: false }),
  setRuntime: (runtime) => set({ runtime }),
  stop: () => set({ activeRoutineId: null, expanded: false, isPaused: true, stopRequested: false, autoStart: false, runtime: null }),
}));
